import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { isSessionLate, sessionTone } from "@/lib/multi-view";
import type { BatchScale } from "@/lib/recipes/staged-method";
import type { CookingSession } from "@/types/cooking-session";
import { SessionActions } from "./SessionActions";
import { SessionStatusPill } from "./SessionStatusPill";
import { SessionTimer } from "./SessionTimer";
import { StageList } from "./StageList";

interface CompactCardProps {
  session: CookingSession;
  seconds: number;
  scale: BatchScale;
  onFocus: () => void;
  onError: (message: string) => void;
}

/**
 * A recipe stacked beside the spotlight (YC-26): its stage, that stage's
 * ingredients, and the actions. Clicking the card moves it into the spotlight.
 */
export function CompactCard({ session, seconds, scale, onFocus, onError }: CompactCardProps) {
  const { t } = useLingui();
  const late = isSessionLate(session, seconds);
  return (
    <article
      aria-label={session.recipeName}
      data-late={late ? "true" : undefined}
      className={cn(
        "flex flex-col gap-3 rounded-2xl border-2 bg-(--color-surface-content) p-3",
        late ? "border-(--color-danger)" : "border-(--color-border)",
      )}
    >
      <button
        type="button"
        onClick={onFocus}
        aria-label={t`Move ${session.recipeName} into the spotlight`}
        className="flex cursor-pointer flex-col gap-2 rounded-lg text-left hover:bg-(--color-surface-muted)"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate text-base font-semibold text-(--color-text-primary)">{session.recipeName}</h3>
          <SessionStatusPill tone={sessionTone(session, seconds)} />
        </div>
        <SessionTimer session={session} seconds={seconds} />
        <span className="text-xs text-(--color-text-secondary)">{session.currentStage?.name ?? t`No stage yet`}</span>
      </button>
      <StageList session={session} scale={scale} onlyCurrent />
      <SessionActions session={session} onError={onError} />
    </article>
  );
}
